import { useState } from "react";
import {
  formatClassTime,
  validateSchedule,
  WEEK_DAYS,
} from "../utils/classTimeUtils";

const buildScheduleFromValue = (classTime) => {
  if (!classTime || classTime === "Not Set") {
    return [];
  }

  if (Array.isArray(classTime)) {
    return classTime
      .filter((group) => group && typeof group === "object")
      .map((group) => ({
        days: Array.isArray(group.days) ? group.days : [],
        startTime: group.startTime || "",
        endTime: group.endTime || "",
      }))
      .filter(
        (group) =>
          Array.isArray(group.days) &&
          group.days.length > 0 &&
          group.startTime &&
          group.endTime,
      );
  }

  return classTime
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(
        /^(.+?):\s*([0-9]{1,2}:[0-9]{2})\s*-\s*([0-9]{1,2}:[0-9]{2})$/,
      );

      if (!match) {
        return null;
      }

      const days = match[1]
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean);

      return {
        days,
        startTime: match[2],
        endTime: match[3],
      };
    })
    .filter(Boolean);
};

const ClassTimingEditor = ({
  courseId,
  value,
  handleEdit,
  notificationCtx,
  canEdit = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const [scheduleGroups, setScheduleGroups] = useState([
    { days: [], startTime: "", endTime: "" },
  ]);

  const startEditing = () => {
    if (!canEdit) return;
    const parsedSchedule = buildScheduleFromValue(value);

    setScheduleGroups(
      parsedSchedule.length > 0
        ? parsedSchedule
        : [{ days: [], startTime: "", endTime: "" }],
    );

    setIsEditing(true);
  };

  const toggleGroupDay = (groupIndex, day) => {
    setScheduleGroups((prev) =>
      prev.map((group, index) => {
        if (index !== groupIndex) return group;

        const updatedDays = group.days.includes(day)
          ? group.days.filter((d) => d !== day)
          : [...group.days, day];

        const orderedDays = WEEK_DAYS.map(
          (dayOption) => dayOption.label,
        ).filter((dayLabel) => updatedDays.includes(dayLabel));

        return {
          ...group,
          days: orderedDays,
        };
      }),
    );
  };

  const updateGroupField = (groupIndex, field, fieldValue) => {
    setScheduleGroups((prev) =>
      prev.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              [field]: fieldValue,
            }
          : group,
      ),
    );
  };

  const addScheduleGroup = () => {
    setScheduleGroups((prev) => [
      ...prev,
      { days: [], startTime: "", endTime: "" },
    ]);
  };

  const removeScheduleGroup = (groupIndex) => {
    setScheduleGroups((prev) =>
      prev.filter((_, index) => index !== groupIndex),
    );
  };

  const handleSave = () => {
    if (!canEdit) return;
    const validationError = validateSchedule(scheduleGroups);

    if (validationError) {
      notificationCtx.show({ message: validationError });
      return;
    }

    handleEdit(courseId, "classTime", scheduleGroups);
    setIsEditing(false);
  };

  const renderedValue = Array.isArray(value)
    ? formatClassTime(value)
    : formatClassTime(buildScheduleFromValue(value));

  return (
    <>
      {/* ================= TABLE CELL ================= */}
      <div
        className="d-flex flex-column align-items-center"
        style={{
          minWidth: "150px",
          gap: "6px",
        }}
      >
        <div
          style={{
            whiteSpace: "pre-line",
            fontSize: "13px",
            lineHeight: "1.5",
            textAlign: "center",
          }}
        >
          {renderedValue}
        </div>

        {canEdit && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={startEditing}
            style={{
              backgroundColor: "#d5d1fd",
              border: "none",
              color: "#403b55",
              fontWeight: "600",
              fontSize: "12px",
              padding: "5px 13px",
              borderRadius: "7px",
            }}
          >
            Edit Schedule
          </button>
        )}
      </div>

      {/* ================= EDIT MODAL ================= */}
      {canEdit && isEditing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(48, 45, 69, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={() => setIsEditing(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "650px",
              maxHeight: "90vh",
              backgroundColor: "#fff",
              borderRadius: "18px",
              boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ================= MODAL HEADER ================= */}
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                padding: "20px 24px",
                backgroundColor: "#d5d1fd",
                flexShrink: 0,
              }}
            >
              <div>
                <h5
                  className="mb-1"
                  style={{
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  Edit Class Schedule
                </h5>

                <small
                  style={{
                    color: "#625d75",
                  }}
                >
                  Manage the days and timings for this course
                </small>
              </div>

              <button
                type="button"
                className="btn-close"
                onClick={() => setIsEditing(false)}
                aria-label="Close"
              />
            </div>

            {/* ================= MODAL BODY ================= */}
            <div
              style={{
                padding: "22px 24px",
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
              }}
            >
              {scheduleGroups.map((group, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "#faf9fd",
                    border: "1px solid #ebe8f4",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "14px",
                  }}
                >
                  {/* Schedule heading */}
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{
                      marginBottom: "14px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: "700",
                          color: "#403b55",
                          fontSize: "14px",
                        }}
                      >
                        Schedule {index + 1}
                      </div>

                      <small
                        style={{
                          color: "#77718b",
                        }}
                      >
                        Select the days and class timing
                      </small>
                    </div>

                    {canEdit && scheduleGroups.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => removeScheduleGroup(index)}
                        style={{
                          backgroundColor: "#f2c4c4",
                          border: "none",
                          color: "#654343",
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "6px 10px",
                          borderRadius: "7px",
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {/* Days */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#514b65",
                        marginBottom: "7px",
                      }}
                    >
                      Class Days
                    </label>

                    <div
                      className="d-flex flex-wrap"
                      style={{
                        gap: "6px",
                      }}
                    >
                      {WEEK_DAYS.map((day) => {
                        const active = group.days.includes(day.label);

                        return (
                          <button
                            key={day.label}
                            type="button"
                            className="btn btn-sm"
                            disabled={!canEdit}
                            onClick={() => toggleGroupDay(index, day.label)}
                            style={{
                              backgroundColor: active ? "#d5d1fd" : "#fff",
                              border: active
                                ? "1px solid #d5d1fd"
                                : "1px solid #e2dfea",
                              color: active ? "#403b55" : "#77718b",
                              fontWeight: active ? "600" : "500",
                              padding: "7px 11px",
                              borderRadius: "7px",
                              fontSize: "12px",
                              cursor: canEdit ? "pointer" : "default",
                              opacity: canEdit ? 1 : 0.7,
                            }}
                          >
                            {day.full}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#514b65",
                        }}
                      >
                        Start Time
                      </label>

                      <input
                        type="time"
                        className="form-control"
                        value={group.startTime}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateGroupField(index, "startTime", e.target.value)
                        }
                        style={{
                          border: "1px solid #e5e1ef",
                          borderRadius: "8px",
                          cursor: canEdit ? "text" : "default",
                        }}
                      />
                    </div>

                    <div className="col-md-6">
                      <label
                        className="form-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#514b65",
                        }}
                      >
                        End Time
                      </label>

                      <input
                        type="time"
                        className="form-control"
                        value={group.endTime}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateGroupField(index, "endTime", e.target.value)
                        }
                        style={{
                          border: "1px solid #e5e1ef",
                          borderRadius: "8px",
                          cursor: canEdit ? "text" : "default",
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add schedule */}
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={addScheduleGroup}
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #d5d1fd",
                    color: "#6259a8",
                    fontWeight: "600",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontSize: "13px",
                  }}
                >
                  + Add Schedule
                </button>
              )}
            </div>

            {/* ================= MODAL FOOTER ================= */}
            <div
              className="d-flex justify-content-end gap-2"
              style={{
                padding: "14px 24px",
                borderTop: "1px solid #eeeaf5",
                backgroundColor: "#fff",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={() => setIsEditing(false)}
                style={{
                  backgroundColor: "#f3f1f7",
                  border: "none",
                  color: "#625d75",
                  fontWeight: "600",
                  padding: "8px 16px",
                  borderRadius: "8px",
                }}
              >
                Cancel
              </button>

              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={handleSave}
                  style={{
                    backgroundColor: "#bff1db",
                    border: "none",
                    color: "#355c4a",
                    fontWeight: "600",
                    padding: "8px 18px",
                    borderRadius: "8px",
                  }}
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClassTimingEditor;
